import { differenceInCalendarDays, startOfDay } from 'date-fns'
import {
    createCanonicalStudentKey,
    haveCompatibleInstruments,
    normalizeOptimizerAvailability,
} from '../../Scheduling2025/scheduleOptimizer'
import { DRIVE_TIME_BUCKET, resolveFamilyPlaceKey } from './summerBreakDriveTimeUtils'

const DAY_NAMES = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
]

const normalize = (value) => String(value || '').trim().toLowerCase()

const studentInstruments = (student) => (
    Array.isArray(student?.instrumentList) && student.instrumentList.length > 0
        ? student.instrumentList
        : (student?.instrumentNames || []).map((name) => ({ name }))
)

const teacherHasAvailability = (teacher) => {
    const normalized = normalizeOptimizerAvailability(teacher?.AvailabilityDetails)
    return Object.values(normalized.byDay).some((blocks) => blocks.length > 0)
}

export const getReplacementStudentsForSchool = (summary, schoolId) => {
    const school = (summary?.schools || []).find(
        (candidate) => normalize(candidate?.id) === normalize(schoolId)
    )
    return (school?.students || []).filter(
        (student) => student?.teacherPlan === 'needs-new-teacher'
    )
}

export const getAvailableTeachersForSchool = (summary, schoolId) => {
    const replacementStudents = getReplacementStudentsForSchool(summary, schoolId)

    return (summary?.teacherDirectory || [])
        .filter((teacher) => normalize(teacher?.School) === normalize(schoolId))
        .filter((teacher) => normalize(teacher?.Status) === 'active')
        .filter((teacher) => teacher?.WantsMoreStudents !== false)
        .filter(teacherHasAvailability)
        .filter((teacher) => replacementStudents.some((student) => (
            haveCompatibleInstruments(
                teacher?.InstrumentList,
                studentInstruments(student)
            )
        )))
        .map((teacher) => {
            const availability = normalizeOptimizerAvailability(teacher?.AvailabilityDetails)
            const availabilityDays = DAY_NAMES.filter(
                (_, dayIndex) => (availability.byDay[dayIndex] || []).length > 0
            )
            const matchingStudentIds = replacementStudents
                .filter((student) => haveCompatibleInstruments(
                    teacher?.InstrumentList,
                    studentInstruments(student)
                ))
                .map((student) => student.studentId)

            return {
                ...teacher,
                availabilityDays,
                matchingStudentIds,
                activeStudentCount: Array.isArray(teacher?.Students)
                    ? teacher.Students.filter((student) => student?.Active !== false).length
                    : null,
            }
        })
        .sort((left, right) => String(left?.Name || '').localeCompare(String(right?.Name || '')))
}

export const buildAvailableTeacherGroups = (summary, schoolId) => {
    const school = (summary?.schools || []).find(
        (candidate) => normalize(candidate?.id) === normalize(schoolId)
    )
    const availableTeachers = getAvailableTeachersForSchool(summary, schoolId)

    return (school?.instruments || [])
        .map((instrument) => {
            const students = (instrument.students || []).filter(
                (student) => student?.teacherPlan === 'needs-new-teacher'
            )
            const teachers = availableTeachers.filter((teacher) => students.some((student) => (
                haveCompatibleInstruments(
                    teacher?.InstrumentList,
                    studentInstruments(student)
                )
            )))

            return {
                instrumentName: instrument.name,
                students,
                teachers,
            }
        })
        .filter((group) => group.students.length > 0)
}

export const resolveTeacherPlaceKey = (teacher, directories) => {
    const teacherId = String(teacher?.TeacherID || teacher?.id || '').trim()
    if (!teacherId) return ''

    const directory = (directories || []).find((entry) => (
        (Array.isArray(entry?.teacherIds) && entry.teacherIds.some(
            (savedTeacherId) => String(savedTeacherId) === teacherId
        ))
        || normalize(entry?.key) === normalize(teacherId)
    ))
    return String(directory?.key || '').trim()
}

export const buildPlannerTeachers = (teachers, directories) => (
    (teachers || []).map((teacher) => ({
        id: String(teacher?.TeacherID || ''),
        name: teacher?.Name || 'Teacher',
        homeKey: resolveTeacherPlaceKey(teacher, directories) || null,
        instruments: teacher?.InstrumentList || [],
        availability: teacher?.AvailabilityDetails || {},
    }))
)

export const buildPlannerStudents = (students, directories) => (
    (students || []).map((student) => ({
        key: `${student.familyId}-${student.studentId}`,
        studentId: student.studentId,
        studentName: student.studentName,
        familyId: student.familyId,
        familyName: student.familyName,
        placeKey: resolveFamilyPlaceKey(student, directories) || null,
        duration: Number(student.lessonLength) || 30,
        instruments: studentInstruments(student),
        availability: student.availability || null,
    }))
)

const parseEventDate = (event) => {
    const candidates = [
        event?.dateTime,
        event?.dateTimeUtc,
        event?.DateTime,
        event?.DateTimeUtc,
        event?.date,
        event?.startDate,
        event?.parsedDate,
        event?.start?.dateTime,
        event?.start?.date,
    ].filter(Boolean)

    for (const candidate of candidates) {
        const date = candidate instanceof Date ? candidate : new Date(candidate)
        if (!Number.isNaN(date.getTime())) return date
    }
    return null
}

const eventDuration = (event, startDate) => {
    const endCandidates = [
        event?.endDateTime,
        event?.EndDateTime,
        event?.end?.dateTime,
        event?.end?.date,
    ].filter(Boolean)

    for (const candidate of endCandidates) {
        const endDate = candidate instanceof Date ? candidate : new Date(candidate)
        const minutes = (endDate.getTime() - startDate.getTime()) / 60000
        if (!Number.isNaN(endDate.getTime()) && minutes > 0) return minutes
    }

    return Math.max(5, Number(event?.lessonLength) || Number(event?.rowLength) * 5 || 30)
}

const resolveEventPlaceKey = (event, directories) => {
    const explicitKey = event?.placeKey || event?.place_key || event?.placeId
    if (explicitKey) return String(explicitKey)

    const familyId = String(event?.familyId || event?.FamilyID || '').trim()
    if (familyId) {
        const familyKey = resolveFamilyPlaceKey({ familyId }, directories)
        if (familyKey) return familyKey
    }

    const labels = [event?.familyName, event?.studentName]
        .map(normalize)
        .filter(Boolean)
    const matchingDirectory = (directories || []).find((entry) => (
        labels.includes(normalize(entry?.label))
        || labels.includes(normalize(entry?.key))
    ))
    return String(matchingDirectory?.key || '').trim() || null
}

export const buildLockedCalendarEvents = ({
    events = [],
    weekStart,
    teacherIds = [],
    directories = [],
}) => {
    const allowedTeacherIds = new Set(teacherIds.map(String))

    return events
        .filter((event) => !event?.isPendingOffer && !event?.isAvailabilityPlaceholder)
        .map((event, index) => {
            const teacherId = String(
                event?.teacherId || event?.teacherID || event?.TeacherID || ''
            )
            const startDate = parseEventDate(event)
            if (!allowedTeacherIds.has(teacherId) || !startDate) return null

            const dayIndex = differenceInCalendarDays(
                startOfDay(startDate),
                startOfDay(weekStart)
            )
            const startMinutes = startDate.getHours() * 60 + startDate.getMinutes()
            const duration = eventDuration(event, startDate)

            return {
                id: String(event?.id || event?._id || `calendar-${teacherId}-${index}`),
                studentKey: createCanonicalStudentKey(event),
                teacherId,
                dayIndex,
                start: startMinutes,
                end: startMinutes + duration,
                duration,
                placeKey: resolveEventPlaceKey(event, directories),
                studentName: event?.studentName || '',
                familyName: event?.familyName || '',
                label: event?.label || event?.name || '',
                locked: true,
                source: 'calendar',
            }
        })
        .filter((event) => event && event.dayIndex >= 0 && event.dayIndex <= 6)
}

export const createDriveTimeLookup = (
    edges,
    bucket = DRIVE_TIME_BUCKET
) => {
    const minutesByPair = new Map()
    ;(edges || []).forEach((edge) => {
        if (edge?.todBucket && edge.todBucket !== bucket) return
        const fromKey = edge?.origin?.key || edge?.fromKey || edge?.from
        const toKey = edge?.destination?.key || edge?.toKey || edge?.to
        const minutes = Number(edge?.minutes)
        if (!fromKey || !toKey || !Number.isFinite(minutes)) return
        minutesByPair.set(`${normalize(fromKey)}=>${normalize(toKey)}`, minutes)
    })

    return (fromKey, toKey) => {
        if (!fromKey || !toKey) return null
        if (normalize(fromKey) === normalize(toKey)) return 0
        return minutesByPair.get(`${normalize(fromKey)}=>${normalize(toKey)}`) ?? null
    }
}

export const formatMinutesAsTime = (minutes) => {
    const normalizedMinutes = Number(minutes)
    if (!Number.isFinite(normalizedMinutes)) return '—'
    const hour24 = Math.floor(normalizedMinutes / 60)
    const minute = normalizedMinutes % 60
    const hour12 = hour24 % 12 || 12
    const suffix = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`
}

export { DAY_NAMES }
